"use client";

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@/utils";

/**
 * Slider — a thin wrapper over Radix Slider. Single-thumb for one value, or two thumbs for a range.
 * Fully keyboard-accessible (arrows, Home/End) with roving focus from Radix. Track/range/thumb use
 * semantic tokens (muted track, primary range + thumb border). For a labelled spectrum
 * (e.g. Formal ↔ Casual) compose endpoint labels around it — the component stays a pure control.
 *
 *   <Slider defaultValue={[50]} max={100} step={1} />
 *   <Slider defaultValue={[25, 75]} max={100} />   // range
 */
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root>) {
  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max],
  );

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          // Track uses primary/20 (matching Progress) — a brand-tinted surface that stays visible
          // in light mode. `bg-muted` renders ~1.1:1 on the white light-mode surface (invisible);
          // the filled range still reads 8–12:1 against this track. See nonTextContrast VRT gate.
          "bg-primary/20 relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            "bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          // Radix does not forward the Root's aria-label to the thumb (which carries role="slider"),
          // so the accessible name must land here or the thumb is unnamed.
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          className="border-primary bg-background ring-ring/50 focus-visible:border-ring block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
